/**
 * BirthdayRewardsTab - Birthday campaign management
 */
import { Settings, Cake, Play, Send, Mail, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { api } from '@/utils/apiClient';
import { DISCOUNT_OPTIONS, SEND_TIMING_OPTIONS } from './automatedUtils';

export default function BirthdayRewardsTab({
  settings,
  upcomingBirthdays,
  sendingBirthday,
  onUpdateSettings,
  onSendBirthdayReward,
  onRefresh
}) {
  const runNow = async () => {
    try {
      await api.post('/automated-campaigns/birthday/run-now');
      toast.success('Birthday campaign triggered');
      setTimeout(onRefresh, 2000);
    } catch (err) {
      toast.error('Failed to trigger campaign');
    }
  };

  if (!settings) return null;

  return (
    <div className="grid md:grid-cols-2 gap-6">
      {/* Settings */}
      <div className="bg-card rounded-xl border border-border p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-medium text-foreground flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Birthday Campaign Settings
          </h3>
          <Button
            size="sm"
            variant="outline"
            onClick={runNow}
            data-testid="birthday-run-now"
          >
            <Play className="w-3 h-3 mr-1" />
            Run Now
          </Button>
        </div>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-sm">Enable Birthday Rewards</p>
              <p className="text-xs text-muted-foreground">Auto-send birthday discounts</p>
            </div>
            <Switch
              checked={settings.enabled}
              onCheckedChange={(checked) => onUpdateSettings({ enabled: checked })}
              data-testid="birthday-enabled-switch"
            />
          </div>
          
          <div className="space-y-1">
            <Label className="text-xs">Discount Percentage (Max 15%)</Label>
            <select
              value={settings.discount_percent}
              onChange={(e) => onUpdateSettings({ discount_percent: parseInt(e.target.value) })}
              className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
              data-testid="birthday-discount-select"
            >
              {DISCOUNT_OPTIONS.map(v => (
                <option key={v} value={v}>{v}% off</option>
              ))}
            </select>
          </div>
          
          <div className="space-y-1">
            <Label className="text-xs">Send Timing</Label>
            <select
              value={settings.days_before}
              onChange={(e) => onUpdateSettings({ days_before: parseInt(e.target.value) })}
              className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
            >
              {SEND_TIMING_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          
          <div className="flex items-center gap-4 pt-2">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={settings.send_sms}
                onChange={(e) => onUpdateSettings({ send_sms: e.target.checked })}
                className="rounded"
              />
              <Phone className="w-4 h-4" /> SMS
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={settings.send_email}
                onChange={(e) => onUpdateSettings({ send_email: e.target.checked })}
                className="rounded"
              />
              <Mail className="w-4 h-4" /> Email
            </label>
          </div>
        </div>
      </div>

      {/* Upcoming Birthdays */}
      <div className="bg-card rounded-xl border border-border p-5">
        <h3 className="font-medium text-foreground mb-4 flex items-center gap-2">
          <Cake className="w-5 h-5 text-pink-500" />
          Upcoming Birthdays ({upcomingBirthdays.length})
        </h3>
        
        <div className="space-y-2 max-h-80 overflow-y-auto">
          {upcomingBirthdays.length === 0 ? (
            <p className="text-center text-muted-foreground py-4">No upcoming birthdays in the next 14 days</p>
          ) : (
            upcomingBirthdays.map((contact) => (
              <div key={contact.contact_id} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                <div>
                  <p className="font-medium text-sm">{contact.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {contact.days_until === 0 ? 'TODAY!' : `In ${contact.days_until} day${contact.days_until > 1 ? 's' : ''}`}
                    {' • '}{contact.birthday}
                  </p>
                </div>
                <Button
                  size="sm"
                  onClick={() => onSendBirthdayReward(contact.contact_id)}
                  disabled={sendingBirthday === contact.contact_id}
                  data-testid={`send-birthday-${contact.contact_id}`}
                >
                  {sendingBirthday === contact.contact_id ? 'Sending...' : (
                    <>
                      <Send className="w-3 h-3 mr-1" /> Send
                    </>
                  )}
                </Button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
